import { useEffect } from 'react';
import { logger } from '../utils/logger';

// De Screen Wake Lock API zit nog niet in de DOM-types van TypeScript 4.9,
// dus we declareren hier het minimale stukje dat we gebruiken.
interface WakeLockSentinelLike {
    release(): Promise<void>;
}

interface NavigatorWithWakeLock extends Navigator {
    wakeLock?: {
        request(type: 'screen'): Promise<WakeLockSentinelLike>;
    };
}

/**
 * Houdt een screen wake lock vast zolang `active` true is, zodat het systeem
 * niet door inactiviteit in slaapstand gaat (bijv. tijdens een opname).
 *
 * De browser geeft het lock automatisch vrij zodra het tabblad onzichtbaar
 * wordt; bij terugkeer naar het tabblad vragen we het daarom opnieuw aan.
 * Een dichtgeklapte laptop kan een wake lock niet voorkomen.
 */
const useWakeLock = (active: boolean): void => {
    useEffect(() => {
        const wakeLock = typeof navigator !== 'undefined'
            ? (navigator as NavigatorWithWakeLock).wakeLock
            : undefined;
        if (!active || !wakeLock) {
            return undefined;
        }

        let released = false;
        let sentinel: WakeLockSentinelLike | null = null;

        const request = async () => {
            try {
                const lock = await wakeLock.request('screen');
                if (released) {
                    await lock.release();
                    return;
                }
                sentinel = lock;
            } catch (error) {
                // Kan falen door browser- of energiebesparingsinstellingen; de
                // opname loopt dan gewoon door, alleen zonder slaap-preventie.
                logger.warn('Wake lock niet beschikbaar:', error);
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void request();
            }
        };

        void request();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            released = true;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            sentinel?.release().catch(() => undefined);
            sentinel = null;
        };
    }, [active]);
};

export default useWakeLock;
